'use client';

import { c as _c } from "react/compiler-runtime";
import { type UseMutationResult, type UseQueryResult, useMutation, useQuery, useInfiniteQuery, type UseInfiniteQueryOptions, useSuspenseQuery, type UseSuspenseQueryResult, keepPreviousData, hashKey, type QueryKey, type QueryFunction } from '@tanstack/react-query';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { type Variables } from 'graphql-request';
import { Filters } from '@funkey/shared/utils';
import type { InputMaybe, PageInfoFragment, Exact } from '@funkey/types/graphql/graphql';
import { useMemo } from 'react';
import throttle from 'lodash/throttle';
import { useGraphQLClient } from './client';
import { getGQLDocumentOperationName } from './shared';
export { getGQLDocumentOperationName } from './shared';
export type GraphQLDocument<O, I> = TypedDocumentNode<O, I>;
export namespace GraphQLDocument {
  export type Type<O, I> = TypedDocumentNode<O, I>;

  /** Removes the graphql document type for empty variables from `T`.
   * If the resulting type is `never`, it means the document doesn't have any input variables */
  export type withoutEmptyExact<T> = Exclude<T, undefined | Exact<{
    [key: string]: never;
  }>>;

  /** Gets the variables input type for a given query/mutation document */
  // prettier-ignore
  export type input<T> = T extends TypedDocumentNode<any, infer I>
  // Removes empty `{}` variables
  ? Exclude<I, undefined | Exact<{
    [key: string]: never;
  }>> extends never ? never : I : never;

  /** Gets the return type for a given query/mutation document */
  // prettier-ignore
  export type output<T> = T extends TypedDocumentNode<infer O, any> ? O : never;
}

// [Query]
type QueryOptions<O> = Omit<Parameters<typeof useQuery<O>>[0], 'queryKey' | 'queryFn'> & {
  /** You shouldn't manually specify the query key.
   * It's inferred from the document, variables and locale  */
  queryKey?: QueryKey;
  /** Amount of ms to throttle between requests */
  throttleMs?: number;
};
export function useGraphqlQuery(document, ...t0) {
  const $ = _c(2);
  const args = t0;
  const [variables, t1] = args;
  const options = t1 === undefined ? {} : t1;
  const graphqlClient = useGraphQLClient();
  const queryKey = options.queryKey ?? [getGQLDocumentOperationName(document) ?? document, graphqlClient.config.locale, variables];
  options.queryKey ? hashKey(options.queryKey) : document;
  let t2;
  bb0: {
    const queryFn = ctx => graphqlClient.request(document, (options.queryKey ? variables : ctx.queryKey[2] as any) as Variables);
    if (!options.throttleMs) {
      t2 = queryFn;
      break bb0;
    }
    let t3;
    if ($[0] !== queryFn) {
      t3 = throttle(queryFn, 1000);
      $[0] = queryFn;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    t2 = t3;
  }
  const query = useQuery({
    ...options,
    queryKey,
    queryFn: t2,
    retry: options.retry ?? false,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    placeholderData: "placeholderData" in options ? options.placeholderData : keepPreviousData
  }) as UseQueryResult<O, unknown>;
  return Object.assign(query, {
    queryKey
  });
}
export type UseGraphqlQueryOptions<O = unknown, I = unknown> = Parameters<typeof useGraphqlQuery<O, I>>[2];
export function createGraphqlQueryHook<O, I>(document: TypedDocumentNode<O, I>) {
  type QueryParams = Parameters<typeof useGraphqlQuery<O, I>>;
  type Var = QueryParams[1];
  type Options = QueryParams[2];
  return (...args: GraphQLDocument.withoutEmptyExact<Var> extends never ? [variables?: Var, options?: Options] : [variables: Var, options?: Options]) =>
  // @ts-ignore
  useGraphqlQuery(document, ...args);
}
namespace useGraphqlSuspenseQuery {
  export type Options = Omit<Parameters<typeof useSuspenseQuery>[0], 'queryKey' | 'queryFn'> & {
    headers?: Record<string, string>;
  };
}
export function useGraphqlSuspenseQuery(document, ...t0) {
  const $ = _c(19);
  const args = t0;
  const [variables, t1] = args;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let headers;
  let rqOptions;
  if ($[2] !== t2) {
    ({
      headers,
      ...rqOptions
    } = t2);
    $[2] = t2;
    $[3] = headers;
    $[4] = rqOptions;
  } else {
    headers = $[3];
    rqOptions = $[4];
  }
  const graphqlClient = useGraphQLClient();
  let t3;
  if ($[5] !== document) {
    t3 = getGQLDocumentOperationName(document) ?? document;
    $[5] = document;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3 || $[8] !== variables) {
    t4 = [t3, variables];
    $[7] = t3;
    $[8] = variables;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== document || $[11] !== graphqlClient || $[12] !== headers || $[13] !== variables) {
    t5 = _ctx => graphqlClient.request(document, variables as Variables, {
      ...graphqlClient.config.headers,
      ...headers
    });
    $[10] = document;
    $[11] = graphqlClient;
    $[12] = headers;
    $[13] = variables;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== rqOptions || $[16] !== t4 || $[17] !== t5) {
    t6 = {
      ...rqOptions,
      queryKey: t4,
      queryFn: t5
    };
    $[15] = rqOptions;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return useSuspenseQuery(t6) as UseSuspenseQueryResult<O, unknown>;
}
export type UseGraphqlSuspenseQueryOptions<O = unknown, I = unknown> = Parameters<typeof useGraphqlSuspenseQuery<O, I>>[2];

// [Mutation]
export function useGraphqlMutation(document, t0) {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const graphqlClient = useGraphQLClient();
  let t2;
  if ($[2] !== document) {
    t2 = getGQLDocumentOperationName(document) ?? document;
    $[2] = document;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = [t2];
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== document || $[7] !== graphqlClient) {
    t4 = variables => graphqlClient.request(document, variables);
    $[6] = document;
    $[7] = graphqlClient;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = options?.retry ?? false;
  let t6;
  if ($[9] !== options || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      ...options,
      mutationKey: t3,
      mutationFn: t4,
      retry: t5
    };
    $[9] = options;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return useMutation(t6) as UseMutationResult<O, unknown, I, unknown>;
}
export type UseGraphqlMutationOptions<O = unknown, I extends Variables | undefined = undefined> = Parameters<typeof useGraphqlMutation<O, I>>[1];
export function createGraphqlMutationHook<O, I extends Variables | undefined>(document: TypedDocumentNode<O, I>) {
  return (options?: UseGraphqlMutationOptions<O, I>) => useGraphqlMutation<O, I>(document, options);
}

// [Infinite query]
export type UseInfiniteGraphQlQueryOptions = Omit<UseInfiniteQueryOptions, 'queryKey' | 'initialPageParam' | 'getNextPageParam'> & {
  getNextPageParam?: (lastPage: any, allPages: any[]) => string | null;
};
export function useGraphqlInfiniteQuery(document, variables, dataKey, options) {
  const $ = _c(28);
  const graphqlClient = useGraphQLClient();
  if (!graphqlClient) {
    throw new Error("No graphqlClient");
  }
  const t0 = options as any;
  let t1;
  if ($[0] !== document) {
    t1 = getGQLDocumentOperationName(document) ?? document;
    $[0] = document;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== variables) {
    t2 = [t1, variables];
    $[2] = t1;
    $[3] = variables;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== document || $[6] !== graphqlClient || $[7] !== variables) {
    t3 = context => graphqlClient.request(document, {
      ...variables,
      after: context.pageParam as I['after']
    });
    $[5] = document;
    $[6] = graphqlClient;
    $[7] = variables;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = options?.retry ?? false;
  const t5 = options?.refetchOnWindowFocus ?? false;
  let t6;
  if ($[9] !== dataKey) {
    t6 = lastPage => {
      if (!lastPage[dataKey].pageInfo?.hasNextPage) {
        return null;
      }
      return lastPage[dataKey].pageInfo.endCursor;
    };
    $[9] = dataKey;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t0 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = {
      ...t0,
      queryKey: t2,
      initialPageParam: null as I['after'],
      queryFn: t3,
      retry: t4,
      refetchOnWindowFocus: t5,
      getNextPageParam: t6
    };
    $[11] = t0;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const result = useInfiniteQuery(t7);
  let t8;
  if ($[18] !== dataKey || $[19] !== result.data?.pages) {
    t8 = result.data?.pages.flatMap(page => (page[dataKey as K] as O[K])?.edges?.map(_temp)).filter(Filters.nonnull);
    $[18] = dataKey;
    $[19] = result.data?.pages;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const dataKeyValue = t8;
  let t9;
  if ($[21] !== dataKey || $[22] !== dataKeyValue) {
    t9 = {
      [dataKey]: dataKeyValue
    };
    $[21] = dataKey;
    $[22] = dataKeyValue;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const value = t9 as unknown as {
    // [dataKey in K]: typeof dataKeyValue
  [dataKey in K]: NonNullable<NonNullable<NonNullable<NonNullable<O[K]>['edges']>[number]>['node']>[] | undefined };
  const t10 = !result.error && result.hasNextPage;
  let t11;
  if ($[24] !== result || $[25] !== t10 || $[26] !== value) {
    t11 = {
      ...result,
      ...value,
      hasMoreRecords: t10
    };
    $[24] = result;
    $[25] = t10;
    $[26] = value;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11 as const;
}
function _temp(edge) {
  return edge?.node;
}
export type UseGraphqlInfiniteQueryOptions = Omit<UseInfiniteQueryOptions, 'queryKey' | 'initialPageParam' | 'queryFn' | 'getNextPageParam'>;